import { useMemo, useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Image as ImageIcon,
  Info,
  Plus,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { Button } from '@/ui/Button';
import { Field, Input, Select, Switch } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { api, errorMessage, ApiError } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { dateTime, humanize, money, num, relativeTime, smartDate } from '@/lib/format';
import {
  activeAction,
  approvalSummary,
  buildConfigRows,
  CONFIG_STATE_META,
  CUSTOM_GROUP,
  CUSTOM_KEY_PREFIX,
  QUEUE_ALARM_DEPTH,
  RECEIPT_LOGO_KEY,
  RECEIPT_LOGO_MAX_BYTES,
  RECEIPT_LOGO_MAX_HEIGHT,
  RECEIPT_LOGO_MAX_WIDTH,
  readRegisterHealth,
  registerLifecycle,
  statusIsMisleading,
  validateConfigValue,
  validateCustomKey,
  validateReceiptLogo,
  type ConfigKeyRow,
} from '@/features/system/registers';
import type {
  CashShift,
  RegisterConfigAsset,
  RegisterConfigBundle,
  RegisterDetail,
  RegisterSyncLogEntry,
  UserSummary,
} from '@/lib/api/types';

/**
 * One register: its health, its configuration, and its lifecycle.
 *
 * The configuration half is a **desired-vs-applied** system and that is the
 * whole value of this screen. `values` is what an administrator asked for;
 * `applied` is what the register reported back on its heartbeat. A key can be
 * saved here and simply never land — because the register is offline, because
 * it errored applying it, or because it is holding a different value than the
 * version it acknowledged. Each of those is a different badge, and each needs a
 * different response from the operator.
 */

export const Route = createFileRoute('/_app/system/registers_/$id')({
  staticData: { crumb: 'Register' },
  component: RegisterDetailScreen,
});

function RegisterDetailScreen() {
  const { id } = Route.useParams();
  const registerId = Number(id);
  const queryClient = useQueryClient();
  const canApprove = useAtLeast('manager');

  const register = useQuery({
    queryKey: ['registers', registerId],
    queryFn: () => api.get<RegisterDetail>(`/registers/${registerId}`),
    refetchInterval: 20_000,
  });

  const config = useQuery({
    queryKey: ['registers', registerId, 'config'],
    queryFn: () => api.get<RegisterConfigBundle>(`/registers/${registerId}/config`),
  });

  const staff = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
    staleTime: 5 * 60_000,
  });

  const cashierName = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of staff.data ?? []) {
      map.set(u.id, `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.username);
    }
    return map;
  }, [staff.data]);

  function invalidateRegister() {
    void queryClient.invalidateQueries({ queryKey: ['registers'] });
  }

  /* ── Lifecycle mutations ─────────────────────────────────────────────── */

  const forceSync = useMutation({
    mutationFn: () => api.post(`/registers/${registerId}/force-sync`),
    onSuccess: () =>
      toast.success('Force sync queued', {
        description:
          'This is advisory. The register picks the request up on its next heartbeat — nothing has synced yet.',
      }),
    onError: (error) =>
      toast.error('Could not queue force sync', { description: errorMessage(error) }),
  });

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      api.patch<RegisterDetail>(`/registers/${registerId}/status`, { active }),
    onSuccess: (updated) => {
      invalidateRegister();
      toast.success(updated.active ? 'Register enabled' : 'Register disabled', {
        description: updated.active
          ? 'Register-authed requests are accepted again.'
          : 'Every register-authed request from this hardware is now refused.',
      });
    },
    onError: (error) =>
      toast.error('Could not change the register', { description: errorMessage(error) }),
  });

  const approve = useMutation({
    mutationFn: () => api.post<RegisterDetail>(`/registers/${registerId}/approve`),
    onSuccess: () => {
      invalidateRegister();
      toast.success('Register approved', {
        description: 'It can now sync, take payments and pull the catalog.',
      });
    },
    onError: (error) =>
      toast.error('Could not approve', { description: errorMessage(error) }),
  });

  const reject = useMutation({
    mutationFn: () =>
      api.post<RegisterDetail>(`/registers/${registerId}/reject`, {
        expectedStatus: 'pending',
      }),
    onSuccess: () => {
      invalidateRegister();
      toast.success('Enrollment rejected');
    },
    onError: (error) => {
      // 409 = the row moved under us. Re-read and report; never retry blindly.
      invalidateRegister();
      const conflict = error instanceof ApiError && error.status === 409;
      toast.error(
        conflict ? 'The register changed while you were looking' : 'Could not reject',
        {
          description: conflict
            ? `${errorMessage(error)} The record has been re-read — check its current state before acting again.`
            : errorMessage(error),
        }
      );
    },
  });

  const [retireOpen, setRetireOpen] = useState(false);
  const retire = useMutation({
    mutationFn: () => api.post<RegisterDetail>(`/registers/${registerId}/retire`),
    onSuccess: () => {
      invalidateRegister();
      setRetireOpen(false);
      toast.success('Register retired', {
        description:
          'Deactivated and its hardware id renamed, so a reinstall enrolls as a new register.',
      });
    },
    onError: (error) => {
      const conflict = error instanceof ApiError && error.status === 409;
      if (conflict) {
        // This 409 is protecting real money: the register still owns live or
        // payment-pending carts. Keep the dialog open and quote the server.
        toast.error('Retire refused — live money on this register', {
          description: errorMessage(error),
          duration: 12_000,
        });
        return;
      }
      toast.error('Could not retire the register', { description: errorMessage(error) });
    },
  });

  if (register.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow="System · Registers" title="Loading…" />
        <PageBody className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-48 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (register.isError || !register.data) {
    return (
      <Page>
        <PageHeader eyebrow="System · Registers" title={`Register ${id}`} />
        <PageBody>
          <ErrorState
            title="Could not load this register"
            message={errorMessage(register.error)}
            onRetry={() => void register.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  const data = register.data;
  const reading = readRegisterHealth(data);
  // Lifecycle comes from approvedAt + active + retired, not the status column
  // (TASK-0207): see registerLifecycle in features/system/registers.ts.
  const lifecycle = registerLifecycle(data);
  const isPending = lifecycle === 'pending';
  // Disable is any staff; Re-enable needs a manager (registers.approve).
  const toggle = activeAction(data, canApprove);
  const queueDepth = data.transactionQueueDepth ?? 0;

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link
            to="/system/registers"
            search={{ q: '', view: 'all' }}
            className="hover:text-ink hover:underline"
          >
            System · Registers
          </Link>
        }
        title={
          <span className="flex items-center gap-2.5">
            {data.name}
            <Badge tone={reading.tone} dot pulse={reading.livePulse}>
              {reading.label}
            </Badge>
          </span>
        }
        subtitle={reading.detail}
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              asChild
            >
              <Link to="/system/registers" search={{ q: '', view: 'all' }}>
                Fleet
              </Link>
            </Button>
            <Button
              size="sm"
              icon={<RefreshCw className="size-3.5" />}
              loading={forceSync.isPending}
              onClick={() => forceSync.mutate()}
            >
              Force sync
            </Button>
            {isPending && canApprove ? (
              <>
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Check className="size-3.5" />}
                  loading={approve.isPending}
                  onClick={() => approve.mutate()}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  icon={<X className="size-3.5" />}
                  loading={reject.isPending}
                  onClick={() => reject.mutate()}
                >
                  Reject
                </Button>
              </>
            ) : null}
            {toggle === 'disable' || toggle === 'reenable' ? (
              <ActiveToggle
                action={toggle}
                name={data.name}
                pending={setActive.isPending}
                onChange={(next) => setActive.mutate(next)}
              />
            ) : null}
            {canApprove && !isPending && lifecycle !== 'retired' ? (
              <Button size="sm" variant="danger" onClick={() => setRetireOpen(true)}>
                Retire
              </Button>
            ) : null}
          </>
        }
      />

      <PageBody className="space-y-4">
        {isPending ? (
          <Callout tone="warn" icon={<ShieldAlert />} title="Waiting for approval">
            This register handshaked but has not been approved. The server refuses
            everything except its heartbeat: no catalog, no sync, no payments.{' '}
            {canApprove
              ? 'Approving it records you as the approver and opens all of that.'
              : 'A manager has to approve it.'}
          </Callout>
        ) : null}

        {toggle === 'reenable-needs-manager' ? (
          <Callout tone="info" icon={<Info />} title="Re-enabling needs a manager">
            This register is disabled, so the server refuses its requests. Only a manager
            can turn it back on: ask one to re-enable it from this page.
          </Callout>
        ) : null}

        {lifecycle === 'rejected' ? (
          <Callout tone="info" icon={<Info />} title="Never approved">
            This record was disabled without ever being approved (a rejected enrollment,
            or a register disabled before approvals were recorded). It cannot be
            re-enabled. To use this machine again, retire the record, then have the
            register enroll again and approve the new enrollment.
          </Callout>
        ) : null}

        {lifecycle === 'retired' ? (
          <Callout tone="info" icon={<Info />} title="Retired">
            This record is closed for good. A reinstall on the same machine enrolls as a
            new register, which then needs its own approval.
          </Callout>
        ) : null}

        {statusIsMisleading(data, reading) ? (
          <Callout tone="warn" icon={<Info />} title="Ignore the status column">
            The stored status is <Code>{data.status}</Code>, but the last heartbeat landed{' '}
            {relativeTime(data.lastHeartbeat)}. Every heartbeat from an approved register
            rewrites that column to <Code>online</Code> and nothing ever writes it back,
            so it is a record of the last contact, not a live signal.
          </Callout>
        ) : null}

        {queueDepth > 0 ? (
          <Callout
            tone={queueDepth >= QUEUE_ALARM_DEPTH ? 'negative' : 'warn'}
            title={`${num(queueDepth)} transactions waiting to sync up`}
          >
            {queueDepth >= QUEUE_ALARM_DEPTH
              ? 'A backlog this size usually means a wedged batch rather than a slow link — sales are sitting on the lane, not on the server.'
              : 'A small queue drains on the next sync. If it keeps climbing, the register is not getting through.'}
          </Callout>
        ) : null}

        <StatRow>
          <Stat
            label="Heartbeat"
            value={data.lastHeartbeat ? relativeTime(data.lastHeartbeat) : 'never'}
            tone={
              reading.health === 'silent'
                ? 'negative'
                : reading.health === 'stale'
                  ? 'warn'
                  : 'positive'
            }
            sub={dateTime(data.lastHeartbeat)}
          />
          <Stat
            label="Queue depth"
            value={num(queueDepth)}
            tone={
              queueDepth >= QUEUE_ALARM_DEPTH
                ? 'negative'
                : queueDepth > 0
                  ? 'warn'
                  : undefined
            }
            sub="unsent transactions"
          />
          <Stat
            label="Last sync"
            value={data.lastSyncAt ? relativeTime(data.lastSyncAt) : '—'}
            sub={dateTime(data.lastSyncAt)}
          />
          <Stat
            label="App version"
            value={data.appVersion ?? 'unknown'}
            sub={data.appVersion ? undefined : 'this build does not report one'}
          />
          <Stat
            label="Current shift"
            value={data.currentShiftId ? `#${data.currentShiftId}` : '—'}
            sub={
              data.currentCashierId
                ? (cashierName.get(data.currentCashierId) ??
                  `User ${data.currentCashierId}`)
                : 'no cashier signed on'
            }
          />
        </StatRow>

        <Panel title="Identity">
          <DetailList columns={3}>
            <Detail label="Register id" mono>
              <Code copyable>{data.id}</Code>
            </Detail>
            <Detail label="Station" mono>
              {data.stationNumber ?? '—'}
            </Detail>
            <Detail label="Stored status">{humanize(data.status)}</Detail>
            <Detail label="Active (revocation flag)">
              <Badge tone={data.active ? 'positive' : 'negative'}>
                {data.active ? 'Active' : 'Revoked'}
              </Badge>
            </Detail>
            {/* An API older than TASK-0207 sends no approvedAt; say nothing
                rather than "Not approved" for a live register. */}
            {data.approvedAt !== undefined ? (
              <Detail label="Approval">
                <span>{approvalSummary(data, (userId) => cashierName.get(userId))}</span>
                {data.approvedAt ? (
                  <span className="text-2xs text-ink-faint block font-mono">
                    {dateTime(data.approvedAt)}
                  </span>
                ) : null}
              </Detail>
            ) : null}
            <Detail label="Enrolled" mono>
              {dateTime(data.createdAt)}
            </Detail>
            <Detail label="Last updated" mono>
              {dateTime(data.updatedAt)}
            </Detail>
          </DetailList>
        </Panel>

        <ConfigurationPanel
          registerId={registerId}
          bundle={config.data}
          loading={config.isLoading}
          error={config.error}
          onRetry={() => void config.refetch()}
        />

        <ReceiptLogoPanel
          registerId={registerId}
          assets={config.data?.assets ?? []}
          loading={config.isLoading}
        />

        <div className="grid gap-3 xl:grid-cols-2">
          <SyncHistoryPanel entries={data.syncHistory ?? []} />
          <ShiftHistoryPanel shifts={data.shifts ?? []} cashierName={cashierName} />
        </div>
      </PageBody>

      <ConfirmDialog
        open={retireOpen}
        onOpenChange={setRetireOpen}
        title={`Retire ${data.name}?`}
        description="Cutover retirement. This is not a pause — the hardware identity is renamed so the machine can never reuse this record."
        confirmLabel="Retire register"
        typeToConfirm={data.name}
        loading={retire.isPending}
        consequences={[
          'Sets active to false — every register-authed request is refused from now on',
          'Sets status to disabled',
          'Renames the hardware id, so a wipe-and-reinstall enrolls as a NEW register instead of colliding local ids against already-synced rows',
          'Refused with a 409 if this register still owns live or payment-pending carts',
        ]}
        onConfirm={() => retire.mutate()}
      />
    </Page>
  );
}

/* ── Enable / disable ─────────────────────────────────────────────────────── */

function ActiveToggle({
  action,
  name,
  pending,
  onChange,
}: {
  action: 'disable' | 'reenable';
  name: string;
  pending: boolean;
  onChange: (next: boolean) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  if (action === 'disable') {
    return (
      <>
        <Button
          size="sm"
          variant="danger"
          loading={pending}
          onClick={() => setConfirming(true)}
        >
          Disable
        </Button>
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={`Disable ${name}?`}
          description="The durable revocation flag. It survives heartbeats, unlike the status column."
          confirmLabel="Disable register"
          loading={pending}
          consequences={[
            'Every register-authed request from this lane is refused immediately',
            'Any transactions still queued on the register cannot sync up until it is re-enabled',
            'Reversible, but only a manager can re-enable it',
          ]}
          onConfirm={() => {
            onChange(false);
            setConfirming(false);
          }}
        />
      </>
    );
  }

  // Only shown to a manager, for an approved register (activeAction): this
  // re-enables it; it can never approve an enrollment.
  return (
    <Button size="sm" loading={pending} onClick={() => onChange(true)}>
      Re-enable
    </Button>
  );
}

/* ── Configuration ────────────────────────────────────────────────────────────
   Draft semantics, which the rest of the panel depends on:
     draft[key] === undefined  untouched
     draft[key] === null       staged clear (server deletes the managed value)
     draft[key] === string     staged value
   The PATCH is all-or-nothing on the server: one bad key rejects the whole
   payload and writes nothing. So the Save button submits the entire draft and
   a 400 leaves every edit intact on screen, ready to be fixed.             */

const GROUP_ORDER = [
  'Hardware',
  'Receipt/Printer',
  'Scanner',
  'Scale',
  'Display',
  CUSTOM_GROUP,
];

function ConfigurationPanel({
  registerId,
  bundle,
  loading,
  error,
  onRetry,
}: {
  registerId: number;
  bundle: RegisterConfigBundle | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [confirmClear, setConfirmClear] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const rows = useMemo(() => (bundle ? buildConfigRows(bundle) : []), [bundle]);

  const save = useMutation({
    mutationFn: (values: Record<string, string | null>) =>
      api.patch<{ changed: unknown[] }>(`/registers/${registerId}/config`, { values }),
    onSuccess: (_result, values) => {
      void queryClient.invalidateQueries({
        queryKey: ['registers', registerId, 'config'],
      });
      setDraft({});
      setConfirmClear(false);
      const cleared = Object.values(values).filter((v) => v === null).length;
      toast.success(`${Object.keys(values).length} setting(s) saved`, {
        description: cleared
          ? `${cleared} cleared. The register removes the local value and applies the rest on its next heartbeat.`
          : 'The register applies these on its next heartbeat — the badges stay Pending until it acknowledges.',
      });
    },
    onError: (error) => {
      setConfirmClear(false);
      // All-or-nothing: nothing was written, so the draft is still exactly
      // what the operator needs to fix.
      toast.error('Nothing was saved', {
        description: `${errorMessage(error)} The whole payload is validated before any write, so no key changed. Your edits are still here.`,
      });
    },
  });

  const dirtyKeys = Object.keys(draft);
  const clearedKeys = dirtyKeys.filter((k) => draft[k] === null);

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    for (const key of dirtyKeys) {
      const value = draft[key];
      if (value === null || value === undefined) continue;
      const row = rows.find((r) => r.key === key);
      if (!row) continue;
      const message = validateConfigValue(row, value);
      if (message) errors[key] = message;
    }
    return errors;
  }, [draft, dirtyKeys, rows]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  function submit() {
    if (clearedKeys.length > 0 && !confirmClear) {
      setConfirmClear(true);
      return;
    }
    save.mutate(draft);
  }

  const grouped = useMemo(() => {
    const map = new Map<string, ConfigKeyRow[]>();
    for (const row of rows) {
      const list = map.get(row.group) ?? [];
      list.push(row);
      map.set(row.group, list);
    }
    return [...map.entries()].sort(
      (a, b) =>
        (GROUP_ORDER.indexOf(a[0]) + 1 || 99) - (GROUP_ORDER.indexOf(b[0]) + 1 || 99)
    );
  }, [rows]);

  const counts = useMemo(() => {
    const tally = { pending: 0, drift: 0, error: 0 };
    for (const row of rows) {
      if (row.state === 'pending') tally.pending += 1;
      if (row.state === 'drift') tally.drift += 1;
      if (row.state === 'error') tally.error += 1;
    }
    return tally;
  }, [rows]);

  if (error) {
    return (
      <Panel title="Configuration">
        <ErrorState message={errorMessage(error)} onRetry={onRetry} />
      </Panel>
    );
  }

  return (
    <>
      <Panel
        title="Configuration"
        description="Desired here · applied on the register. The badge is the gap between the two."
        actions={
          <>
            <Button
              size="xs"
              variant="ghost"
              icon={<Plus className="size-3" />}
              onClick={() => setAddOpen(true)}
            >
              Custom key
            </Button>
            {dirtyKeys.length > 0 ? (
              <>
                <Button size="xs" variant="ghost" onClick={() => setDraft({})}>
                  Revert
                </Button>
                <Button
                  size="xs"
                  variant="primary"
                  loading={save.isPending}
                  disabled={hasErrors}
                  onClick={submit}
                >
                  Save {dirtyKeys.length}
                </Button>
              </>
            ) : null}
          </>
        }
        footer={
          loading ? undefined : (
            <span className="flex flex-wrap items-center gap-3">
              <span>{rows.filter((r) => r.state === 'applied').length} applied</span>
              {counts.pending > 0 ? (
                <span className="text-warn">{counts.pending} pending</span>
              ) : null}
              {counts.drift > 0 ? (
                <span className="text-negative">{counts.drift} drifted</span>
              ) : null}
              {counts.error > 0 ? (
                <span className="text-negative">{counts.error} errored</span>
              ) : null}
              {hasErrors ? (
                <span className="text-negative ml-auto">
                  Fix the highlighted values — the server rejects the whole payload
                  otherwise.
                </span>
              ) : null}
            </span>
          )
        }
      >
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <div className="space-y-5">
            {grouped.map(([group, groupRows]) => (
              <section key={group}>
                <h3 className="border-line-soft text-2xs text-ink-faint mb-2 border-b pb-1 font-medium tracking-wider uppercase">
                  {group}
                </h3>
                {groupRows.length === 0 ? (
                  <p className="text-2xs text-ink-faint">Nothing here.</p>
                ) : (
                  <div className="space-y-3">
                    {groupRows.map((row) => (
                      <ConfigRow
                        key={row.key}
                        row={row}
                        draft={draft}
                        error={fieldErrors[row.key] ?? null}
                        onChange={(value) =>
                          setDraft((prev) => ({ ...prev, [row.key]: value }))
                        }
                        onReset={() =>
                          setDraft((prev) => {
                            const next = { ...prev };
                            delete next[row.key];
                            return next;
                          })
                        }
                      />
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </Panel>

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title={
          clearedKeys.length === 1
            ? 'Clear this setting?'
            : `Clear ${clearedKeys.length} settings?`
        }
        description="Clearing removes the managed value. The register then deletes its local copy of the setting and falls back to its own default."
        confirmLabel="Clear and save"
        loading={save.isPending}
        consequences={[
          ...clearedKeys.map((key) => `Clears ${key}`),
          'The register applies the removal on its next heartbeat',
        ]}
        onConfirm={() => save.mutate(draft)}
      />

      <AddCustomKeyDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        existing={rows.map((r) => r.key)}
        onAdd={(key, value) => {
          setDraft((prev) => ({ ...prev, [key]: value }));
          setAddOpen(false);
        }}
      />
    </>
  );
}

function ConfigRow({
  row,
  draft,
  error,
  onChange,
  onReset,
}: {
  row: ConfigKeyRow;
  draft: Record<string, string | null>;
  error: string | null;
  onChange: (value: string | null) => void;
  onReset: () => void;
}) {
  const staged = Object.prototype.hasOwnProperty.call(draft, row.key);
  const stagedValue = staged ? draft[row.key] : undefined;
  const serverValue = row.desired?.value ?? '';
  const value = staged ? (stagedValue ?? '') : serverValue;
  const willClear = staged && stagedValue === null;
  const meta = CONFIG_STATE_META[row.state];

  // Every definition the API returns today is `register`-owned; `global` and
  // `both` exist in the contract, and a global key is rejected outright by the
  // PATCH validator — so it must never render as an editable control.
  const globalOwned = row.ownership === 'global';
  const readOnly = globalOwned;

  const control = (() => {
    if (readOnly) {
      return (
        <div className="text-ink-muted flex h-8 items-center px-0.5 font-mono text-xs">
          {serverValue || row.defaultValue || '—'}
        </div>
      );
    }
    if (row.type === 'boolean') {
      return (
        <div className="flex h-8 items-center">
          <Switch
            checked={value === 'true'}
            onCheckedChange={(next) => onChange(String(next))}
            label={value === 'true' ? 'On' : 'Off'}
          />
        </div>
      );
    }
    if (row.type === 'enum' && row.enumOptions.length > 0) {
      return (
        <Select
          size="sm"
          value={value}
          invalid={!!error}
          onChange={(e) => onChange(e.currentTarget.value)}
        >
          <option value="">
            {row.defaultValue ? `Default (${row.defaultValue})` : 'Not set'}
          </option>
          {row.enumOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      );
    }
    return (
      <Input
        size="sm"
        mono
        value={value}
        invalid={!!error}
        inputMode={row.type === 'integer' ? 'numeric' : undefined}
        placeholder={row.defaultValue ? `Default: ${row.defaultValue}` : 'Not set'}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    );
  })();

  return (
    <div
      className={
        willClear
          ? 'border-negative/30 bg-negative-muted grid grid-cols-1 gap-2 rounded border p-2 lg:grid-cols-[minmax(0,1fr)_16rem_minmax(0,1fr)]'
          : staged
            ? 'border-signal-line bg-signal-muted grid grid-cols-1 gap-2 rounded border p-2 lg:grid-cols-[minmax(0,1fr)_16rem_minmax(0,1fr)]'
            : 'grid grid-cols-1 gap-2 p-2 lg:grid-cols-[minmax(0,1fr)_16rem_minmax(0,1fr)]'
      }
    >
      {/* Identity */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-ink truncate text-sm">{row.label}</span>
          <Badge tone={meta.tone}>{meta.label}</Badge>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <Code>{row.key}</Code>
          <span className="text-2xs text-ink-faint">{row.type}</span>
          {globalOwned ? (
            <span className="text-2xs text-warn">
              global — set in Settings, not per register
            </span>
          ) : row.ownership === 'both' ? (
            <span className="text-2xs text-ink-faint">
              overrides the global value for this register
            </span>
          ) : null}
        </div>
        {row.note ? <p className="text-2xs text-ink-faint mt-1">{row.note}</p> : null}
      </div>

      {/* Control */}
      <div className="min-w-0">
        {control}
        {error ? <p className="text-2xs text-negative mt-1">{error}</p> : null}
        {willClear ? (
          <p className="text-2xs text-negative mt-1">
            Will be cleared — the register deletes its local value.
          </p>
        ) : null}
        {!readOnly ? (
          <div className="mt-1 flex items-center gap-2">
            {staged ? (
              <button
                type="button"
                className="text-2xs text-ink-faint hover:text-ink underline-offset-2 hover:underline"
                onClick={onReset}
              >
                Undo edit
              </button>
            ) : null}
            {row.desired && !willClear ? (
              <button
                type="button"
                className="text-2xs text-ink-faint hover:text-negative flex items-center gap-1 underline-offset-2 hover:underline"
                onClick={() => onChange(null)}
              >
                <Trash2 className="size-3" /> Clear
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* What the register actually reports */}
      <div className="text-2xs min-w-0">
        <p className="text-ink-faint">{meta.hint}</p>
        <dl className="text-ink-muted mt-1 space-y-0.5 font-mono">
          <div className="flex gap-2">
            <dt className="text-ink-faint w-20 shrink-0">desired</dt>
            <dd className="min-w-0 break-all">
              {row.desired
                ? `v${row.desired.desiredVersion} · ${row.desired.value ?? '(cleared)'}`
                : '—'}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-ink-faint w-20 shrink-0">applied</dt>
            <dd className="min-w-0 break-all">
              {row.applied
                ? `v${row.applied.appliedVersion} · ${row.applied.value ?? '(not reported)'}`
                : 'never reported'}
            </dd>
          </div>
          {row.applied?.lastAppliedAt ? (
            <div className="flex gap-2">
              <dt className="text-ink-faint w-20 shrink-0">at</dt>
              <dd>{smartDate(row.applied.lastAppliedAt)}</dd>
            </div>
          ) : null}
        </dl>
        {row.applied?.lastError ? (
          <p className="border-negative/30 bg-negative-muted text-negative mt-1 rounded border px-1.5 py-1 font-mono">
            {row.applied.lastError}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function AddCustomKeyDialog({
  open,
  onOpenChange,
  existing,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: string[];
  onAdd: (key: string, value: string) => void;
}) {
  const [key, setKey] = useState(CUSTOM_KEY_PREFIX);
  const [value, setValue] = useState('');

  const error = (() => {
    const message = validateCustomKey(key);
    if (message) return message;
    if (existing.includes(key.trim())) return 'That key is already on this register.';
    return null;
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setKey(CUSTOM_KEY_PREFIX);
          setValue('');
        }
        onOpenChange(next);
      }}
      title="Add a custom setting"
      description="Custom keys are a server-enforced allowlist: anything not prefixed custom. is rejected."
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!!error}
            onClick={() => onAdd(key.trim(), value)}
          >
            Stage key
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Key"
          error={error}
          hint="Lower-case, dot-separated, e.g. custom.lane_label"
        >
          <Input
            mono
            value={key}
            invalid={!!error}
            onChange={(e) => setKey(e.currentTarget.value)}
          />
        </Field>
        <Field label="Value" hint="Stored verbatim as a string.">
          <Input mono value={value} onChange={(e) => setValue(e.currentTarget.value)} />
        </Field>
        <p className="text-2xs text-ink-faint">
          Staging only adds it to the pending edits — nothing is written until you save
          the configuration panel.
        </p>
      </div>
    </Dialog>
  );
}

/* ── Receipt logo ─────────────────────────────────────────────────────────── */

function ReceiptLogoPanel({
  registerId,
  assets,
  loading,
}: {
  registerId: number;
  assets: RegisterConfigAsset[];
  loading: boolean;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<string | null>(null);

  const logos = assets.filter((a) => a.key === RECEIPT_LOGO_KEY);
  const current = logos[0] ?? null;

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return api.post<RegisterConfigAsset>(
        `/registers/${registerId}/config/assets/receipt-logo`,
        body
      );
    },
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({
        queryKey: ['registers', registerId, 'config'],
      });
      toast.success('Receipt logo uploaded', {
        description: `${asset.width}×${asset.height}, ${Math.round(asset.sizeBytes / 1024)}KB. The register downloads it on its next sync.`,
      });
    },
    onError: (error) =>
      toast.error('Upload rejected', { description: errorMessage(error) }),
  });

  async function pick(file: File | undefined) {
    setRejection(null);
    if (!file) return;
    const check = await validateReceiptLogo(file);
    if (!check.ok) {
      // Say exactly which constraint failed, before anything crosses the wire.
      setRejection(check.error);
      return;
    }
    upload.mutate(file);
  }

  return (
    <Panel
      title="Receipt logo"
      description={`PNG only · ${RECEIPT_LOGO_MAX_BYTES / 1024}KB max · ${RECEIPT_LOGO_MAX_WIDTH}×${RECEIPT_LOGO_MAX_HEIGHT} max`}
      actions={
        <Button
          size="xs"
          icon={<Upload className="size-3" />}
          loading={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          Upload PNG
        </Button>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={(e) => {
          void pick(e.currentTarget.files?.[0]);
          e.currentTarget.value = '';
        }}
      />

      {rejection ? (
        <Callout tone="negative" title="That file cannot be used" className="mb-3">
          {rejection}
        </Callout>
      ) : null}

      {loading ? (
        <Skeleton className="h-16 w-full" />
      ) : current ? (
        <DetailList columns={3}>
          <Detail label="Dimensions" mono>
            {current.width ?? '?'}×{current.height ?? '?'}
          </Detail>
          <Detail label="Size" mono>
            {Math.round(current.sizeBytes / 1024)} KB
          </Detail>
          <Detail label="Uploaded" mono>
            {smartDate(current.createdAt)}
          </Detail>
          <Detail label="Content type" mono>
            {current.contentType}
          </Detail>
          <Detail label="Digest" mono className="lg:col-span-2">
            <Code copyable>{current.sha256.slice(0, 16)}…</Code>
          </Detail>
        </DetailList>
      ) : (
        <EmptyState
          compact
          icon={<ImageIcon />}
          title="No receipt logo"
          description="The register prints text-only receipts until one is uploaded."
        />
      )}

      <p className="text-2xs text-ink-faint mt-3">
        Uploading also bumps the <Code>{RECEIPT_LOGO_KEY}</Code> config key, so the change
        shows up above as Pending until the register confirms it pulled the asset.
      </p>
    </Panel>
  );
}

/* ── History ──────────────────────────────────────────────────────────────── */

function SyncHistoryPanel({ entries }: { entries: RegisterSyncLogEntry[] }) {
  const columns: Array<Column<RegisterSyncLogEntry>> = [
    {
      id: 'when',
      header: 'When',
      width: '7rem',
      mono: true,
      cell: (row) => <span title={row.syncedAt}>{smartDate(row.syncedAt)}</span>,
    },
    {
      id: 'direction',
      header: 'Direction',
      width: '5.5rem',
      cell: (row) => (
        <Badge tone={row.direction === 'up' ? 'info' : 'neutral'}>
          {humanize(row.direction)}
        </Badge>
      ),
    },
    {
      id: 'table',
      header: 'Table',
      cell: (row) => (
        <span className="text-ink-muted truncate">{row.tableName ?? '—'}</span>
      ),
    },
    {
      id: 'records',
      header: 'Records',
      width: '5rem',
      align: 'right',
      mono: true,
      cell: (row) => num(row.recordCount ?? 0),
    },
  ];

  return (
    <Panel title="Sync history" description="The last 20 sync batches." bleed>
      <DataTable
        rows={entries}
        columns={columns}
        rowKey={(row) => row.id}
        keyboard={false}
        empty={{
          title: 'No sync activity',
          description: 'This register has not synced yet.',
        }}
      />
    </Panel>
  );
}

function ShiftHistoryPanel({
  shifts,
  cashierName,
}: {
  shifts: CashShift[];
  cashierName: Map<number, string>;
}) {
  const columns: Array<Column<CashShift>> = [
    {
      id: 'opened',
      header: 'Opened',
      width: '7rem',
      mono: true,
      cell: (row) => <span title={row.openedAt}>{smartDate(row.openedAt)}</span>,
    },
    {
      id: 'cashier',
      header: 'Cashier',
      cell: (row) => (
        <span className="text-ink-muted truncate">
          {row.cashierId
            ? (cashierName.get(row.cashierId) ?? `User ${row.cashierId}`)
            : 'Unassigned'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '5.5rem',
      cell: (row) => (
        <Badge
          tone={row.status === 'open' ? 'signal' : 'neutral'}
          dot={row.status === 'open'}
        >
          {humanize(row.status)}
        </Badge>
      ),
    },
    {
      id: 'variance',
      header: 'Variance',
      width: '6rem',
      align: 'right',
      mono: true,
      cell: (row) =>
        row.variance === null || row.variance === undefined ? (
          <span className="text-ink-faint">—</span>
        ) : (
          <span
            className={
              row.variance < 0
                ? 'text-negative'
                : row.variance > 0
                  ? 'text-positive'
                  : undefined
            }
          >
            {money(row.variance)}
          </span>
        ),
    },
  ];

  return (
    <Panel title="Shift history" description="The last 20 drawers on this lane." bleed>
      <DataTable
        rows={shifts}
        columns={columns}
        rowKey={(row) => row.id}
        keyboard={false}
        empty={{
          title: 'No shifts',
          description: 'No drawer has been opened on this register.',
        }}
      />
    </Panel>
  );
}
